import { useState, useEffect } from 'react';
import { LAYOUT_CONFIG } from '../../../constants';

/**
 * Custom hook for responsive design detection
 * Provides screen width, device type detection, and responsive utilities
 */
export const useResponsive = () => {
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );
  const [screenHeight, setScreenHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 768,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Device type detection
  const isMobile = screenWidth < LAYOUT_CONFIG.MOBILE_BREAKPOINT;
  const isTablet =
    screenWidth >= LAYOUT_CONFIG.MOBILE_BREAKPOINT && screenWidth < LAYOUT_CONFIG.TABLET_BREAKPOINT;
  const isDesktop = screenWidth >= LAYOUT_CONFIG.TABLET_BREAKPOINT;
  const isPortrait = screenHeight > screenWidth;

  // Touch device detection
  const isTouchDevice =
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // Viewport constraints
  const isSmallHeight = screenHeight < 600;
  const isNarrowWidth = screenWidth < LAYOUT_CONFIG.MIN_WIDTH;

  // Responsive utilities
  const getResponsiveValue = (mobileValue, tabletValue, desktopValue) => {
    if (isMobile) return mobileValue;
    if (isTablet) return tabletValue;
    return desktopValue;
  };

  const getTouchFriendlySize = (baseSize) => {
    if (isMobile || isTouchDevice) {
      return Math.max(baseSize, LAYOUT_CONFIG.MIN_TOUCH_TARGET);
    }
    return baseSize;
  };

  return {
    screenWidth,
    screenHeight,
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    isSmallHeight,
    isNarrowWidth,
    isPortrait,
    getResponsiveValue,
    getTouchFriendlySize,
  };
};

/**
 * Hook for detecting if horizontal scroll is needed
 * Useful for font selection and color palette components
 */
export const useHorizontalScroll = (containerRef, itemCount, itemWidth, gap = 8) => {
  const [needsScroll, setNeedsScroll] = useState(false);
  const { screenWidth } = useResponsive();

  useEffect(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const totalItemsWidth = itemCount * itemWidth + (itemCount - 1) * gap;

    setNeedsScroll(totalItemsWidth > containerWidth);
  }, [screenWidth, itemCount, itemWidth, gap, containerRef]);

  return needsScroll;
};
